import { Directive } from '@angular/core';
import { DesignBlockBaseViewComponent, DialogEvents, IFrameMessageType, IFrameReadyMessage, IFrameUserActionType, RecaptchaMode, SimpleDialogData, WebsiteMode } from '@sb/library/base';
import { WebsiteRenderMode } from '@sb/library/data-models';
import { DialogWarningComponent } from '@sb/library/dialog';
import { WidgetManualDeviceTypeManager } from '@sb/library/widget';
import { IFrameComponent, iframeResizer } from 'iframe-resizer';
import { Observable, Subject, filter, map } from 'rxjs';

declare const window: Window & {
    dataLayer: Record<string, any>[];
    gtag: (...args: any) => void;
    fbq: (...args: any) => void;
    parentIFrame: any;
};

@Directive()
export abstract class DesignBlockIframeBaseViewComponent extends DesignBlockBaseViewComponent {
    readonly WebsiteRenderMode = WebsiteRenderMode;
    readonly RecaptchaMode = RecaptchaMode;
    
    protected _iframe: IFrameComponent | null = null;
    protected _iframeLoaded: Subject<void> = new Subject();

    override ngOnInit() {
        super.ngOnInit();

        if (this.Scope.renderContext.RenderMode == WebsiteRenderMode.Widget) {
            window['iFrameResizer' as any] = {
                onReady: () => {
                    //Set callback for updates on full window size
                    window.parentIFrame?.sendMessage(new IFrameReadyMessage({
                        TrackingCode: this.Scope.scriptCodes.map(code => code.ScriptCode).join('\n')
                    }));
                    window.parentIFrame?.getPageInfo((obj: any) => {
                        this.UpdateBreakpoint(obj.clientWidth, obj.clientHeight);
                    });
                }
            } as any;

            this._subscriptions.push(this.DeviceTypeManager.breakPointChange.subscribe(_ => {
                this._iframeLoaded.next();
                this.DetectChanges();
            }));
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this._iframe && this._iframe.iFrameResizer) {
            this._iframe.iFrameResizer.close();
        }
    }

    Load(iframe: HTMLIFrameElement) {
        if (!this._iframe) {
            const components = iframeResizer({
                log: false,
                heightCalculationMethod: 'taggedElement',
                checkOrigin: false,
                onMessage: (signal: any) => {
                    ////////////////////////////////////////////////////////////////////////////////////////////////////////
                    // Any changes to this code must be reflected in Website/Widget/v4/startup.js on the backend
                    // Because startup.js is vanilla JavaScript, we do not use types as well as we should below
                    // When copying code over, make sure to remove "this." and all types from code
                    // Carefully review lines that should be commented/uncommented after the code copy

                    if (!signal || !signal.message) {
                        return;
                    }

                    switch (signal.message.Type) {
                        case IFrameMessageType.Ready:
                            iframe.style.minHeight = '';
                            // Line below should be uncommented in Website/Widget/v4/startup.js
                            // this.RunTrackingCodeScript(signal.message.TrackingCode);
                            break;
                        case IFrameMessageType.Refresh:
                            window.location.reload();
                            break;
                        case IFrameMessageType.Scroll:
                            this.ScrollIframe(signal.message.OffsetTop, signal.message.OffsetBottom);
                            break;
                        case IFrameMessageType.UserAction:
                            switch (signal.message.ActionType) {
                                case IFrameUserActionType.Initiate:
                                    // Not implemented yet
                                    break;
                                case IFrameUserActionType.Submit:
                                    this.TrackFormSubmitted(signal.message.FormTitle, signal.message.FormType);
                                    break;
                            }
                            break;
                    }
                },
            } as any, iframe);

            this._iframe = components && components.length > 0 ? components[0] : null;
        }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Functions starting from here need to be kept in sync with Website/Widget/v4/startup.js on the backend
    // Because startup.js is vanilla JavaScript, we do not use types as well as we should below
    // When copying code over, make sure to remove "this." and all types from code
    // IMPORTANT NOTE: For the startup.js version, change TrackFormSubmitted - the property name should become widget_title (not form_title)

    private ScrollIframe(offsetTop: number, offsetBottom: number) {
        const scrollContainers = this.GetScrollContainers(this._iframe);
        const iFrameTop = this._iframe!.getBoundingClientRect().top;

        offsetTop += iFrameTop - 75;                           // Fudge factor of 75 in case 3rd party website has header
        offsetBottom += iFrameTop - window.innerHeight + 75;   // Fudge factor of 75 in case 3rd party website has footer

        if (offsetTop < 0) {
            if (scrollContainers.length) {
                scrollContainers.forEach(container => container.scrollBy({ top: offsetTop, behavior: 'smooth' }));
            } else {
                this._iframe?.scrollIntoView({ block: 'start', behavior: 'smooth'});
            }
        } else if (offsetBottom > 0) {
            if (scrollContainers.length) {
                scrollContainers.forEach(container => container.scrollBy({ top: offsetBottom, behavior: 'smooth' }));
            } else {
                this._iframe?.scrollIntoView({ block: 'end', behavior: 'smooth'});
            }
        }

        // Support outer iframe scroll-to-error, however they want to do it. We just emit the top and bottom offsets of the thing we want to scroll into view.
        // Line below should be uncommented in Website/Widget/v4/startup.js
        // window.parent?.postMessage({ sbIframeScroll: { offsetTop, offsetBottom }}, '*');
    }

    private GetScrollContainers(element: HTMLElement | null) {
        const scrollContainers: HTMLElement[] = [];

        while (element) {
            if (element.scrollHeight > element.clientHeight) {
                scrollContainers.push(element);
            }
            element = element.parentElement;
        }

        return scrollContainers;
    }

    private TrackFormSubmitted(formTitle: string, formType: string): void {
        // Using try/catch instead of an if-else just to be safe if something goes wrong in the methods
        try {
            window.gtag('event', formType, {
                form_title: formTitle,
                url: window.location.href
            });
        } catch {}

        try {
            window.fbq('trackCustom', formType, {
                form_title: formTitle,
                url: window.location.href
            });
        } catch {}

        // Support outer iframe form submission tracking, however they want to do it.
        // Line below should be uncommented in Website/Widget/v4/startup.js
        // window.parent?.postMessage({ sbFormSubmission: { formTitle, formType }}, '*');
    }

    // End of functions that need to be synchronized with Website/Widget/v4/startup.js on the backend
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    protected UpdateBreakpoint(width: number, height: number) {
        (this.DeviceTypeManager as WidgetManualDeviceTypeManager).SetPageSize(width, height);
    }

    protected OpenDialog429ApiError(): Observable<boolean> {
        return this.CreateDialog(DialogWarningComponent, new SimpleDialogData({
            Title: $localize`:@@CommonTooManySubmissionsTitle:Too Many Submissions`,
            Text: $localize`:@@CommonTooManySubmissionsText:Oops! It seems like we're experiencing a lot of submissions from your location right now. Please try again in a moment or contact #SUPPORT_EMAIL_ADDRESS# for assistance.`
        })).events.pipe(filter(event => event == DialogEvents.PrimaryAction), map(_ => true));
    }

    protected get parentIframe(): any | undefined {
        return window.parentIFrame;
    }

    get isViewOnly(): boolean {
        return this.Scope.websiteMode !== WebsiteMode.Live;
    }
}
